// =============================================================================
// Lairo Status — auth context.
// Google sign-in via Firebase Auth. A user is an admin if their verified email
// is on the @lairo.ai domain, OR they're explicitly on the `admins/{email}`
// allowlist (for outside collaborators). The same check is enforced server-side
// by Firestore rules — this client check is just for UX.
// =============================================================================
import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut as fbSignOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, googleProvider, db } from "./firebase";

// Anyone with a verified email on this domain is an admin.
const ADMIN_DOMAIN = "lairo.ai";
const isDomainAdmin = (u) =>
  !!u?.email && u.emailVerified && u.email.toLowerCase().endsWith(`@${ADMIN_DOMAIN}`);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (isDomainAdmin(u)) {
        setIsAdmin(true);
      } else if (u?.email) {
        // Not on the domain — fall back to the explicit allowlist.
        try {
          const snap = await getDoc(doc(db, "admins", u.email.toLowerCase()));
          setIsAdmin(snap.exists());
        } catch (e) {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
  }, []);

  const signIn = async () => {
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      if (e?.code !== "auth/popup-closed-by-user" && e?.code !== "auth/cancelled-popup-request") {
        setError(e?.message || "Sign-in failed.");
      }
    }
  };

  const signOut = () => fbSignOut(auth);

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, error, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
